import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { Mousewheel } from 'swiper/modules';

import { Slider } from '@atlas/components';
import { useApplicationState, useSettings } from '@atlas/core';
import { ChipsItem, Icon, Label } from '@atlas/ui';

import { useIsRtl } from 'hooks/useIsRtl';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN } from 'src/common/utils';
import requestBuilder from 'src/common/utils/requestBuilder';

import InfoGame from '../components/InfoGame';

const fetchRecentlyPlayedGames = lang =>
  requestBuilder
    .create('/api/gs/recently-played', { method: 'GET' })
    .execute({ query: { locale: lang } })
    .then(response => (Array.isArray(response) ? response : response?.games || []))
    .then(games => games.slice(0, 10));

const LobbyRecentlyPlayedGamesBlock = ({ blockTitleKey, imageUrl }) => {
  const swiperRef = useRef(null);
  const prevSlide = useCallback(() => swiperRef.current?.slidePrev(), []);
  const nextSlide = useCallback(() => swiperRef.current?.slideNext(), []);
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = useCallback(() => toggleVisibilityImg(false), []);

  const [isLocked, setIsLocked] = useState(false);

  const { authenticated } = useApplicationState();
  const { lang } = useSettings();

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const [fetchedGames, setFetchedGames] = useState(null);

  useEffect(() => {
    if (!authenticated) {
      setFetchedGames([]);
      return;
    }

    fetchRecentlyPlayedGames(lang)
      .then(setFetchedGames)
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch recently played games', err);
        setFetchedGames([]);
      });
  }, [authenticated, lang]);

  const finalItems = useMemo(() => {
    if (!fetchedGames) {
      return [];
    }

    return fetchedGames.map(game => ({
      finalImageUrl: game.iconUrl ? imageUrlCDN(game.iconUrl, CDN_TYPE_GAME) : null,
      gId: game.id,
      tileGame: {
        ...game,
        providerName: game.providerName || game.providerDisplayedName,
      },
    }));
  }, [fetchedGames]);

  // Hide the block if user is not authenticated or while loading / has no recently played games
  const shouldHideBlock = !authenticated || !fetchedGames || fetchedGames.length === 0;

  if (shouldHideBlock) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-recently-game">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={32}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/recent-played-games.png`
                }
                width={32}
              />
            </div>
          )}

          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          {!isLocked && (
            <>
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-left"
                label={<Icon name="arrow-left" />}
                onClick={prevSlide}
              />
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-right"
                label={<Icon name="arrow-right" />}
                onClick={nextSlide}
              />
            </>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-recently-game__slider">
          <Slider
            autoplay={false}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            modules={[Mousewheel]}
            mousewheel={{ forceToAxis: true }}
            onBeforeInit={swiper => {
              swiperRef.current = swiper;
            }}
            onLock={() => setIsLocked(true)}
            onUnlock={() => setIsLocked(false)}
            onUpdate={swiperInstance => setIsLocked(swiperInstance.isLocked)}
            slidesPerView={'auto'}
            spaceBetween={8}
          >
            {finalItems.map(({ finalImageUrl, gId, tileGame }) => (
              <Slider.Slide key={gId}>
                <InfoGame game={tileGame} imageOverride={finalImageUrl} size="L" />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyRecentlyPlayedGamesBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default LobbyRecentlyPlayedGamesBlock;
